import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AdicionalDTO, PrintPayload, PrintTalaoPayload, SellItemDTO } from '../models/print.models';
import { PosDetailWithOrders } from '../models/pos.model';
import { WebSerialPrinterService } from './webserial-printer.service';
import { WebUsbPrinterService } from './webusb-printer.service';

// Define a minimal type for the user fields required by printing
export interface PrintUserContext {
  id_utilizador: number | string;
  id_funcionario: number | string;
  empresa: any;
  bancos?: any[];
}

// Options for printing a rasterized PDF to ESC/POS
export interface PdfPrintOptions {
  printerDotsPerLine: number; // 384 for 58mm, 576 for 80mm
  dither: boolean;
  marginTopPx: number;
  marginBottomPx: number;
}

@Injectable({ providedIn: 'root' })
export class PrintService {
  private baseUrl = environment.api;

  constructor(
    private http: HttpClient,
    private wsPrinter: WebSerialPrinterService,
    private usbPrinter: WebUsbPrinterService
  ) {}

  /**
   * Print invoice/receipt directly via ESC/POS commands
   */
  async printInvoiceDirect(payload: PrintPayload): Promise<boolean> {
    const p = this.getActivePrinter();
    if (p?.isSupported) {
      await p.ensureOpen();

      const empresa = payload.utilizador?.empresa;
      const empresaNome = empresa?.nome || 'NOS ERP';
      const nif = empresa?.num_contribuinte || '';
      const morada = empresa?.morada || '';

      // 1. Header (Centered)
      p.printLines([empresaNome], { align: 'center', bold: true });
      if (nif) p.printLines([`NIF: ${nif}`], { align: 'center' });
      if (morada) p.printLines([morada], { align: 'center' });

      const separator = '- '.repeat(20);
      p.printLines([separator], { align: 'center' });

      // 2. Meta Info (Two columns)
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0] + ' ' + now.toTimeString().split(' ')[0].substring(0, 5);
      const docType = payload.fatura?.tipo_factura === 'TVD' ? 'Talão de Venda N:' : 'Factura N:';
      const docNum = payload.fatura?.IUD || '';

      p.table([
        { width: 22, align: 'left' },
        { width: 20, align: 'right' }
      ]).row(dateStr, docType);

      p.table([
        { width: 22, align: 'left' },
        { width: 20, align: 'right' }
      ]).row('Original', docNum);

      p.printLines([`Emitido por: ${empresaNome}`]);
      p.printLines([separator], { align: 'center' });

      // 3. Customer Info
      const clientNome = payload.client?.nome || 'Consumidor final';
      const clientNif = payload.client?.nif || '000000000';
      p.printLines([clientNome]);
      p.printLines([`Contribuinte: ${clientNif}`]);
      p.feed(1);

      // 4. Items Table
      p.table([
        { width: 32, align: 'left' },
        { width: 10, align: 'right' }
      ]).row('IVA DESCRICAO', 'VALOR');
      p.printLines([separator], { align: 'center' });

      // Group VAT by rate; legend codes (I1, I2, ...) follow order of appearance
      const vatSummary = new Map<number, { code: string; base: number; vat: number }>();
      let total = 0;
      for (const item of payload.list_sell) {
        const line = this.calculateLineAmounts(item);
        total += line.total;

        if (!vatSummary.has(line.rate)) {
          vatSummary.set(line.rate, { code: `I${vatSummary.size + 1}`, base: 0, vat: 0 });
        }
        const summary = vatSummary.get(line.rate);
        summary.base += line.base;
        summary.vat += line.vat;

        // Format as "(I1) PRODUCT NAME"
        p.table([
          { width: 32, align: 'left' },
          { width: 10, align: 'right' }
        ]).row(`(${summary.code}) ${item.nome}`, line.total.toFixed(2));
      }
      p.feed(1);

      // 5. Totals
      p.table([
        { width: 32, align: 'left' },
        { width: 10, align: 'right' }
      ]).row('TOTAL', total.toFixed(2));

      p.printLines([separator], { align: 'center' });

      // Print each payment method
      let totalEntregue = 0;
      if (payload.pagamentos && payload.pagamentos.length > 0) {
        for (const pg of payload.pagamentos) {
          if (pg.num > 0) {
            p.table([
              { width: 32, align: 'left' },
              { width: 10, align: 'right' }
            ]).row(pg.type.toUpperCase(), pg.num.toFixed(2));
            totalEntregue += pg.num;
          }
        }
      } else {
        // Fallback to cash if no payment info provided
        p.table([
          { width: 32, align: 'left' },
          { width: 10, align: 'right' }
        ]).row('DINHEIRO', total.toFixed(2));
        totalEntregue = total;
      }

      p.printLines([separator], { align: 'center' });

      p.table([
        { width: 32, align: 'left' },
        { width: 10, align: 'right' }
      ]).row('TOTAL ENTREGUE', totalEntregue.toFixed(2));

      if (payload.troco && payload.troco > 0) {
        p.table([
          { width: 32, align: 'left' },
          { width: 10, align: 'right' }
        ]).row('TROCO', payload.troco.toFixed(2));
      }

      p.printLines([separator], { align: 'center' });

      // 6. VAT Summary
      p.printLines(['RESUMO DO IVA']);
      p.table([
        { width: 14, align: 'left' },
        { width: 14, align: 'center' },
        { width: 14, align: 'right' }
      ]).row('TAXA', 'BASE', 'VALOR');

      let totalBase = 0;
      let totalVat = 0;
      for (const [rate, summary] of [...vatSummary.entries()].sort(([a], [b]) => a - b)) {
        totalBase += summary.base;
        totalVat += summary.vat;
        p.table([
          { width: 14, align: 'left' },
          { width: 14, align: 'center' },
          { width: 14, align: 'right' }
        ]).row(`(${summary.code}) ${rate.toFixed(2).replace('.', ',')}%`, summary.base.toFixed(2), summary.vat.toFixed(2));
      }

      p.table([
        { width: 14, align: 'left' },
        { width: 14, align: 'center' },
        { width: 14, align: 'right' }
      ]).row('TOTAL', totalBase.toFixed(2), totalVat.toFixed(2));

      p.feed(2);

      // 7. Footer
      p.printLines(['Processado por nôsERP'], { align: 'center' });
      p.printLines(['Obrigado pela preferência!'], { align: 'center' });

      p.feed(3);
      await p.print();
      return true;
    }

    return false;
  }

  /**
   * Print invoice/receipt (Talão de Venda / Fatura) to ESC/POS printer
   * Calls Laravel backend endpoint POST /api/print
   */
  printInvoice(data: PrintPayload): Observable<any> {
    return this.http.post(`api/print`, data);
  }

  /** Options used when printing a PDF to the receipt printer */
  public defaultPdfPrintOptions = {
    printerDotsPerLine: 384 as number, // 58mm defaults; set 576 for 80mm
    dither: true as boolean,
    marginTopPx: 8 as number,
    marginBottomPx: 16 as number
  };

  /**
   * Line total, taxable base and VAT for a receipt line, mirroring noserp:
   * a VAT-inclusive `preco` already contains the VAT, otherwise VAT is added on top.
   */
  private calculateLineAmounts(item: SellItemDTO): { rate: number; total: number; base: number; vat: number } {
    const rate = Number(item.iva) || 0;
    const amount = Math.round((item.preco || 0) * (item.quant || 1));
    const isVatInclusive = item.iva_treatment === 'inclusive';
    const vat = isVatInclusive ? Math.round(amount - amount / (1 + rate / 100)) : Math.round((amount * rate) / 100);

    return isVatInclusive ? { rate, total: amount, base: amount - vat, vat } : { rate, total: amount + vat, base: amount, vat };
  }

  /**
   * High-level: print a PDF Blob via Web Serial ESC/POS by extracting text and printing lines.
   * Note: This uses pdfjs-dist to read text content from the PDF and sends it to the
   * ESC/POS printer using WebSerialPrinterService.printLines.
   */
  private getActivePrinter(): any {
    const transport = (localStorage.getItem('printerTransport') as 'serial' | 'usb' | null) || 'serial';
    if (transport === 'usb' && this.usbPrinter?.isSupported) return this.usbPrinter;
    return this.wsPrinter;
  }

  async printPdfBlob(pdfBlob: Blob, opts?: Partial<PdfPrintOptions>) {
    try {
      if (!pdfBlob) {
        throw new Error('printPdfBlob: No PDF Blob provided');
      }

      // Dynamically import pdfjs to avoid bundling worker setup issues
      const pdfjsModule = await import('pdfjs-dist');
      const pdfjs: any = (pdfjsModule as any).default || pdfjsModule;

      // Ensure worker is set (use CDN fallback to avoid Angular asset wiring)
      try {
        const version = pdfjs.version || '2.16.105';
        if (pdfjs.GlobalWorkerOptions) {
          pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${version}/build/pdf.worker.min.js`;
        }
      } catch (e) {
        console.warn('pdfjs worker configuration failed:', e);
      }

      const arrayBuffer = await pdfBlob.arrayBuffer();
      const data = new Uint8Array(arrayBuffer);

      console.log('PDF data size:', data.length);
      console.log('PDF type:', pdfBlob.type);

      if (pdfBlob.type === 'application/json' || data.length < 100) {
        const text = await pdfBlob.text();
        if (text.trim().startsWith('{')) {
          console.error('Expected PDF but received JSON:', text);
          throw new Error('Invalid PDF structure: Received JSON error from server');
        }
      }

      if (data.length > 0) {
        const headerText = Array.from(data.slice(0, 10))
          .map(b => String.fromCharCode(b))
          .join('');
        console.log('PDF header (text):', headerText);
      }

      // Basic validation: Check for %PDF- header
      // Note: Some PDFs might have some leading bytes, but usually they start with %PDF-
      const pdfHeader = [0x25, 0x50, 0x44, 0x46, 0x2d]; // %PDF-
      const isPdf = pdfHeader.every((b, i) => data[i] === b);

      if (!isPdf) {
        const headerBytes = Array.from(data.slice(0, 5))
          .map(b => b.toString(16).padStart(2, '0'))
          .join(' ');
        console.error('Invalid PDF header detected (hex):', headerBytes);
        // Don't throw here yet, let pdfjs try to parse it, as it might handle leading junk
      }

      const loadingTask = pdfjs.getDocument({
        data,
        verbosity: 0,
        disableWorker: true // Try disabling worker for better compatibility and easier debugging
      });
      const pdf = await loadingTask.promise;

      const allLines: string[] = [];

      for (let pageNo = 1; pageNo <= pdf.numPages; pageNo++) {
        const page = await pdf.getPage(pageNo);
        const textContent = await page.getTextContent();
        const items = (textContent.items || []) as any[];

        // Map text items with approximate positions
        const mapped = items
          .filter(it => typeof it?.str === 'string' && it.str.length)
          .map(it => {
            const t = (it as any).transform || [0, 0, 0, 0, 0, 0]; // [a, b, c, d, e, f]
            const x = typeof t[4] === 'number' ? t[4] : 0;
            const y = typeof t[5] === 'number' ? t[5] : 0;
            return { str: (it as any).str as string, x, y };
          });

        // Group items by Y with tolerance, sort lines top->bottom and chars left->right
        const tolerance = 3; // px
        mapped.sort((a, b) => (a.y === b.y ? a.x - b.x : b.y - a.y));

        const groups: { y: number; items: { str: string; x: number }[] }[] = [];
        for (const m of mapped) {
          let g = groups.find(gr => Math.abs(gr.y - m.y) <= tolerance);
          if (!g) {
            g = { y: m.y, items: [] };
            groups.push(g);
          }
          g.items.push({ str: m.str, x: m.x });
        }

        groups.sort((a, b) => b.y - a.y);

        for (const g of groups) {
          g.items.sort((a, b) => a.x - b.x);
          let line = g.items.map(i => i.str).join('');
          // Normalize: convert NBSPs, collapse repeated spaces, trim end only
          line = line
            .replace(/\u00A0/g, ' ')
            .replace(/\t/g, '    ')
            .replace(/\s{2,}/g, ' ');
          allLines.push(line);
        }

        // Add a blank line between pages to visually separate
        if (pageNo < pdf.numPages) {
          allLines.push('');
        }
      }

      const p = this.getActivePrinter();
      if (p?.isSupported && allLines.length) {
        p.ensureOpen();
        // Print all lines left-aligned
        p.printLines(allLines, { align: 'left' });
        p.feed(2);
        p.print();
      }

      return allLines;
    } catch (err) {
      console.error('printPdfBlob error:', err);
      throw err;
    }
  }

  /** Parse adicional JSON string to AdicionalDTO array */
  parseAdicional(adicionalStr: string | null | undefined): AdicionalDTO[] | undefined {
    if (!adicionalStr) return undefined;
    try {
      const parsed = JSON.parse(adicionalStr as any);
      return Array.isArray(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }

  /** Collect all SellItemDTO across all orders in a POS */
  buildSellItemsFromPos(pos: PosDetailWithOrders): SellItemDTO[] {
    const items: SellItemDTO[] = [];
    pos?.orders?.forEach(order => {
      order?.items?.forEach(item => {
        items.push({
          id_produto: Number(item.product.id),
          nome: item.product.name,
          quant: item.quantity,
          preco: item.product.price?.EUR ?? 0,
          desconto: 0,
          tipo_desconto: 'V',
          adicional: this.parseAdicional((item as any).adicional ?? null),
          observacao: (item as any).observacao
        });
      });
    });
    return items;
  }

  /** Build a full PrintTalaoPayload from pos and user context */
  buildTalaoPayload(pos: PosDetailWithOrders, user: PrintUserContext): PrintTalaoPayload {
    return {
      utilizador: {
        id_utilizador: Number(user.id_utilizador),
        id_funcionario: Number(user.id_funcionario),
        empresa: user.empresa,
        bancos: user.bancos ?? []
      },
      list_sell: this.buildSellItemsFromPos(pos),
      id_mesa: Number(pos.pos.id),
      id_banco: null,
      total: 10
    };
  }

  printControlReceipt(pos: PosDetailWithOrders, user: PrintUserContext, total = 0) {
    if (!pos?.orders) {
      return throwError(() => new Error('Missing orders'));
    }
    const payload = this.buildTalaoPayload(pos, user);

    if (!payload.list_sell?.length) {
      return throwError(() => new Error('No items to print'));
    }

    const p = this.getActivePrinter();
    if (p?.isSupported) {
      p.ensureOpen();

      const empresaNome = payload.utilizador?.empresa?.nome || 'NOS ERP';

      // Header
      p.printLines([empresaNome], { align: 'center', bold: true });

      p.printLines(['TALAO DE CONTROLO'], { align: 'center' });
      p.feed(1);

      // Meta
      p.printLines([`Mesa: ${payload.id_mesa ?? ''}`]);
      p.printLines([`Operador: ${payload.utilizador?.id_funcionario ?? ''}`]);

      p.feed(1);

      // Items
      if (Array.isArray(payload.list_sell.reverse())) {
        for (const item of payload.list_sell) {
          const qty = item.quant ?? 1;
          const name = item.nome ?? '';
          const price = item.preco ?? 0;
          p.table([
            { width: 32, align: 'left' }, // adjust to your printer width
            { width: 10, align: 'right' }
          ]).row(`${qty} x ${name}`, (price * qty).toFixed(2));

          if (item?.adicional?.length) {
            for (const ad of item.adicional) {
              const aName = (ad as any)?.nome || (ad as any)?.name || 'Adicional';
              p.printLines([`  + ${aName}`]);
            }
          }

          if (item?.observacao) {
            p.printLines([`  Obs: ${item.observacao}`]);
          }
        }
      }

      // Footer
      p.feed(1);
      if (typeof payload.total === 'number') {
        p.table([
          { width: 32, align: 'left' }, // adjust to your printer width
          { width: 10, align: 'right' }
        ]).row(`Total:`, `$ ${total.toFixed(2)}`);
      }
      p.printLines([new Date().toLocaleString()]);

      p.feed(3);

      p.print();
    }
  }
}
